import WeightChart from './charts/WeightChart'

const PRINT_THEME = {
  cardB: '#e5e7eb', muted: '#9ca3af', accent: '#16a34a', warn: '#f59e0b', card: '#fff',
}

export default function PrintReport({ patient, onClose }) {
  const today = new Date().toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })
  const latest = patient.allMetrics?.[0]
  const oldest = patient.allMetrics?.length > 1 ? patient.allMetrics[patient.allMetrics.length - 1] : null

  const delta = (key) => {
    if (!latest || !oldest || latest[key] == null || oldest[key] == null) return null
    return Math.round((latest[key] - oldest[key]) * 10) / 10
  }

  const weightData = patient.allMetrics
    ?.filter(m => m.peso_kg)
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
    .map(m => ({
      label: new Date(m.fecha).toLocaleDateString('es', { day: '2-digit', month: 'short' }),
      fecha: m.fecha,
      v: m.peso_kg
    })) ?? []

  const edad = patient.fecha_nacimiento
    ? Math.floor((new Date() - new Date(patient.fecha_nacimiento)) / (365.25 * 24 * 3600 * 1000))
    : null

  const tipoLabel = {
    deficit: 'Pérdida de grasa',
    mantenimiento: 'Mantenimiento',
    superavit: 'Ganancia muscular',
  }

  return (
    <div className="print-report-root" style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 100, overflow: 'auto', fontFamily: 'DM Sans, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&family=DM+Mono:wght@400;500&display=swap');
        @media print {
          .no-print { display: none !important; }
          body { background: #fff; }
          body * { visibility: hidden; }
          .print-report-root, .print-report-root * { visibility: visible; }
          .print-report-root {
            position: absolute !important;
            inset: 0 !important;
            left: 0 !important;
            top: 0 !important;
            overflow: visible !important;
            height: auto !important;
          }
        }
      `}</style>

      {/* Toolbar */}
      <div className="no-print" style={{
        position: 'sticky', top: 0, background: '#f9fafb', color: '#111',
        borderBottom: '1px solid #e5e7eb', padding: '12px 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10
      }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>Vista previa del informe</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db',
            background: '#fff', color: '#111', cursor: 'pointer', fontFamily: 'DM Sans', fontSize: 14
          }}>← Volver</button>
          <button onClick={() => {
  const fecha = new Date().toLocaleDateString('es').replace(/\//g, '-')
  const nombre = patient.nombre.replace(/ /g, '_')
  document.title = `${nombre}_${fecha}`
  window.print()
  setTimeout(() => { document.title = 'NutriControl' }, 1000)
}} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: '#16a34a', color: '#fff', cursor: 'pointer',
            fontFamily: 'DM Sans', fontWeight: 700, fontSize: 14
          }}>🖨️ Imprimir / Guardar PDF</button>
        </div>
      </div>

      {/* Informe */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 32px', color: '#111' }}>

        {/* Cabecera */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #111', paddingBottom: 16, marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 2, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
              Informe clínico nutricional
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: -0.5 }}>{patient.nombre}</div>
            <div style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>
              {edad ? `${edad} años` : ''}{edad && patient.sexo ? ' · ' : ''}{patient.sexo === 'M' ? 'Hombre' : patient.sexo === 'F' ? 'Mujer' : ''}
              {patient.altura_cm ? ` · ${patient.altura_cm} cm` : ''}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 13, color: '#6b7280', fontFamily: 'DM Mono, monospace' }}>
            <div>Generado: {today}</div>
            {patient.goals?.tipo && <div style={{ marginTop: 4 }}>Objetivo: {tipoLabel[patient.goals.tipo]}</div>}
          </div>
        </div>

        {/* Composición corporal */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, color: '#6b7280', textTransform: 'uppercase', marginBottom: 12, borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
            Composición corporal actual
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
            {[
              ['Peso', latest?.peso_kg ? `${latest.peso_kg} kg` : '—', delta('peso_kg'), 'kg'],
              ['Grasa corporal', latest?.grasa_pct ? `${latest.grasa_pct}%` : '—', delta('grasa_pct'), '%'],
              ['Masa magra', latest?.masa_magra_pct ? `${latest.masa_magra_pct}%` : '—', delta('masa_magra_pct'), '%'],
              ['Perím. abdominal', latest?.perimetro_abdominal_cm ? `${latest.perimetro_abdominal_cm} cm` : '—', delta('perimetro_abdominal_cm'), 'cm'],
            ].map(([l, v, d, u]) => (
              <div key={l} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{l}</div>
                <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'DM Mono, monospace' }}>{v}</div>
                {d !== null && (
                  <div style={{ fontSize: 12, marginTop: 4, color: d <= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                    {d > 0 ? '+' : ''}{d} {u}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Objetivos corporales */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, color: '#6b7280', textTransform: 'uppercase', marginBottom: 12, borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
            Objetivos corporales
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              ['Peso objetivo', patient.goals?.peso_objetivo ? `${patient.goals.peso_objetivo} kg` : '—'],
              ['Grasa objetivo', patient.goals?.grasa_objetivo_pct ? `${patient.goals.grasa_objetivo_pct}%` : '—'],
            ].map(([l, v]) => (
              <div key={l} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{l}</div>
                <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'DM Mono, monospace' }}>{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Pauta nutricional */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, color: '#6b7280', textTransform: 'uppercase', marginBottom: 12, borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
            Pauta nutricional prescrita
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                {['Nutriente', 'Objetivo diario'].map(h => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, letterSpacing: 0.5, fontWeight: 600, color: '#374151' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['🔥 Calorías', patient.goals?.kcal_objetivo ? `${patient.goals.kcal_objetivo} kcal` : '—'],
                ['🥩 Proteína', patient.goals?.proteina_objetivo ? `${patient.goals.proteina_objetivo} g` : '—'],
                ['🥑 Grasas', patient.goals?.grasa_objetivo ? `${patient.goals.grasa_objetivo} g` : '—'],
                ['🍞 Carbohidratos', patient.goals?.carbohidratos_objetivo ? `${patient.goals.carbohidratos_objetivo} g` : '—'],
              ].map(([l, v]) => (
                <tr key={l} style={{ background: '#fff' }}>
                  <td style={{ padding: '9px 12px', fontWeight: 600 }}>{l}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'DM Mono, monospace' }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Gráfica peso */}
        {weightData.length >= 2 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, color: '#6b7280', textTransform: 'uppercase', marginBottom: 12, borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              Evolución del peso
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
              <WeightChart data={weightData} goal={patient.goals?.peso_objetivo} theme={PRINT_THEME} width={500} />
            </div>
          </div>
        )}

        {/* Historial mediciones */}
        {patient.allMetrics?.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, color: '#6b7280', textTransform: 'uppercase', marginBottom: 12, borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              Historial de mediciones
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f3f4f6' }}>
                  {['Fecha', 'Peso', 'Grasa', 'Masa magra', 'Perímetro', 'Método'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#374151' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {patient.allMetrics.slice(0, 8).map((m, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px 10px' }}>{new Date(m.fecha).toLocaleDateString('es')}</td>
                    <td style={{ padding: '8px 10px', fontFamily: 'DM Mono, monospace' }}>{m.peso_kg ? `${m.peso_kg} kg` : '—'}</td>
                    <td style={{ padding: '8px 10px', fontFamily: 'DM Mono, monospace' }}>{m.grasa_pct ? `${m.grasa_pct}%` : '—'}</td>
                    <td style={{ padding: '8px 10px', fontFamily: 'DM Mono, monospace' }}>{m.masa_magra_pct ? `${m.masa_magra_pct}%` : '—'}</td>
                    <td style={{ padding: '8px 10px', fontFamily: 'DM Mono, monospace' }}>{m.perimetro_abdominal_cm ? `${m.perimetro_abdominal_cm} cm` : '—'}</td>
                    <td style={{ padding: '8px 10px', color: '#6b7280', textTransform: 'capitalize' }}>{m.metodo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9ca3af' }}>
          <span>NutriControl · Informe confidencial · Uso clínico exclusivo</span>
          <span>{today}</span>
        </div>
      </div>
    </div>
  )
}